import React, { useState, useEffect } from 'react';
import { FaCog, FaSave, FaInfoCircle } from 'react-icons/fa';
import { AdminAffiliateApiService } from '../../../service/admin/adminAffiliateApiService';
import './PayoutSettings.css';

const defaultProgram = {
  eligibleThreshold: 20000,
  registrationBonus: 500,
  bonusDaysAfterDelivery: 15,
};

export default function PayoutSettings() {
  // ✅ Default settings (UI safe)
  const defaultSettings = {
    minimumWithdrawal: 1000,
    payoutMethods: ['Bank Transfer', 'UPI', 'PayPal'],
    processingTime: '3-5 business days',
    autoApproveThreshold: 5000,
    taxDeduction: {
      enabled: true,
      rate: 10,
      tdsThreshold: 50000,
    },
    payoutSchedule: {
      frequency: 'Weekly',
      days: ['Friday'],
      cutoffTime: '18:00',
    },
    notificationSettings: {
      emailNotifications: true,
      smsNotifications: false,
      payoutConfirmation: true,
      rejectionReasons: true,
    },
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [programSettings, setProgramSettings] = useState(defaultProgram);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [payoutRes, programRes] = await Promise.all([
        AdminAffiliateApiService.getPayoutSettings(),
        AdminAffiliateApiService.getAffiliateProgramSettings(),
      ]);
      const apiSettings = payoutRes?.data;
      if (apiSettings && typeof apiSettings === 'object') {
        setSettings(normalizeSettings(apiSettings));
      }
      const programData = programRes?.data;
      if (programData && typeof programData === 'object') {
        setProgramSettings({
          eligibleThreshold: programData.eligibleThreshold ?? defaultProgram.eligibleThreshold,
          registrationBonus: programData.registrationBonus ?? defaultProgram.registrationBonus,
          bonusDaysAfterDelivery: programData.bonusDaysAfterDelivery ?? defaultProgram.bonusDaysAfterDelivery,
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Merge API response with defaults (prevents undefined.includes errors)
  const normalizeSettings = (incoming) => {
    const s = incoming && typeof incoming === 'object' ? incoming : {};

    return {
      ...defaultSettings,
      ...s,
      payoutMethods: Array.isArray(s.payoutMethods) ? s.payoutMethods : defaultSettings.payoutMethods,
      taxDeduction: {
        ...defaultSettings.taxDeduction,
        ...(s.taxDeduction && typeof s.taxDeduction === 'object' ? s.taxDeduction : {}),
      },
      payoutSchedule: {
        ...defaultSettings.payoutSchedule,
        ...(s.payoutSchedule && typeof s.payoutSchedule === 'object' ? s.payoutSchedule : {}),
        days: Array.isArray(s?.payoutSchedule?.days) ? s.payoutSchedule.days : defaultSettings.payoutSchedule.days,
      },
      notificationSettings: {
        ...defaultSettings.notificationSettings,
        ...(s.notificationSettings && typeof s.notificationSettings === 'object' ? s.notificationSettings : {}),
      },
    };
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await Promise.all([
        AdminAffiliateApiService.updatePayoutSettings(settings),
        AdminAffiliateApiService.updateAffiliateProgramSettings(programSettings),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProgram = (key, value) => {
    const num = ['eligibleThreshold', 'registrationBonus', 'bonusDaysAfterDelivery'].includes(key)
      ? parseInt(String(value).replace(/\D/g, '') || '0', 10) : value;
    setProgramSettings((prev) => ({ ...prev, [key]: num }));
  };

  // ✅ Safe deep update (creates missing objects/arrays)
  const updateSetting = (path, value) => {
    setSettings((prev) => {
      const newSettings = structuredClone
        ? structuredClone(prev)
        : JSON.parse(JSON.stringify(prev));

      const keys = path.split('.');
      let current = newSettings;

      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (current[k] == null || typeof current[k] !== 'object') current[k] = {};
        current = current[k];
      }

      current[keys[keys.length - 1]] = value;

      // ✅ Ensure arrays never become undefined
      if (!Array.isArray(newSettings.payoutMethods)) newSettings.payoutMethods = [];
      if (!newSettings.taxDeduction || typeof newSettings.taxDeduction !== 'object') newSettings.taxDeduction = { ...defaultSettings.taxDeduction };
      if (!newSettings.payoutSchedule || typeof newSettings.payoutSchedule !== 'object') newSettings.payoutSchedule = { ...defaultSettings.payoutSchedule };
      if (!Array.isArray(newSettings.payoutSchedule.days)) newSettings.payoutSchedule.days = [];
      if (!newSettings.notificationSettings || typeof newSettings.notificationSettings !== 'object') newSettings.notificationSettings = { ...defaultSettings.notificationSettings };

      return newSettings;
    });
  };

  // ✅ Safe selectors used in render (prevents includes on undefined)
  const payoutMethods = Array.isArray(settings?.payoutMethods) ? settings.payoutMethods : [];
  const payoutDays = Array.isArray(settings?.payoutSchedule?.days) ? settings.payoutSchedule.days : [];

  return (
    <div className="affiliate-payout-settings">
      <div className="affiliate-payout-header">
        <h1>Payout Setting</h1>
        <p>Configure eligibility, bonuses, payout methods, schedules, and approval processes for the affiliate program.</p>
      </div>

      <div className="affiliate-payout-info">
        <FaInfoCircle />
        <span>These values control what affiliates see on their dashboard and how registration bonuses and payouts are calculated.</span>
      </div>

      <div className="affiliate-settings-container">
        <div className="affiliate-settings-section">
          <h2>Eligibility & bonus</h2>
          <div className="affiliate-setting-item">
            <label>Eligible threshold (₹)</label>
            <input
              type="number"
              min={0}
              value={programSettings.eligibleThreshold}
              onChange={(e) => updateProgram('eligibleThreshold', e.target.value)}
              className="affiliate-payout-input"
            />
            <small>Delivered sales a referred seller must reach for the affiliate to earn the registration bonus.</small>
          </div>
          <div className="affiliate-setting-item">
            <label>Registration bonus (₹ per eligible seller)</label>
            <input
              type="number"
              min={0}
              value={programSettings.registrationBonus}
              onChange={(e) => updateProgram('registrationBonus', e.target.value)}
              className="affiliate-payout-input"
            />
            <small>Amount credited to the affiliate for each referred seller who crosses the threshold (after the delay below).</small>
          </div>
          <div className="affiliate-setting-item">
            <label>Bonus days after delivery</label>
            <input
              type="number"
              min={0}
              value={programSettings.bonusDaysAfterDelivery}
              onChange={(e) => updateProgram('bonusDaysAfterDelivery', e.target.value)}
              className="affiliate-payout-input"
            />
            <small>Days after the seller first crosses the threshold before the registration bonus is credited.</small>
          </div>
        </div>

        <div className="affiliate-settings-section">
          <h2>General payout</h2>
          <div className="affiliate-setting-item">
            <label>Minimum Withdrawal Amount (₹)</label>
            <input
              type="number"
              value={settings.minimumWithdrawal}
              onChange={(e) => updateSetting('minimumWithdrawal', parseInt(e.target.value || '0', 10))}
              className="affiliate-payout-input"
            />
          </div>

          <div className="affiliate-setting-item">
            <label>Processing Time</label>
            <input
              type="text"
              value={settings.processingTime}
              onChange={(e) => updateSetting('processingTime', e.target.value)}
              className="affiliate-payout-input"
            />
          </div>

          <div className="affiliate-setting-item">
            <label>Auto-approve Threshold (₹)</label>
            <input
              type="number"
              value={settings.autoApproveThreshold}
              onChange={(e) => updateSetting('autoApproveThreshold', parseInt(e.target.value || '0', 10))}
              className="affiliate-payout-input"
            />
            <small>Withdrawals below this amount will be auto-approved</small>
          </div>
        </div>

        <div className="affiliate-settings-section">
          <h2>Payout Methods</h2>
          <div className="affiliate-payout-methods">
            {['Bank Transfer', 'UPI', 'PayPal', 'Payoneer', 'Cheque'].map((method) => (
              <div key={method} className="affiliate-method-item">
                <label className="affiliate-checkbox-label">
                  <input
                    type="checkbox"
                    checked={payoutMethods.includes(method)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateSetting('payoutMethods', Array.from(new Set([...payoutMethods, method])));
                      } else {
                        updateSetting('payoutMethods', payoutMethods.filter((m) => m !== method));
                      }
                    }}
                  />
                  <span>{method}</span>
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="affiliate-settings-section">
          <h2>Tax Settings</h2>
          <div className="affiliate-setting-item">
            <label className="affiliate-checkbox-label">
              <input
                type="checkbox"
                checked={!!settings?.taxDeduction?.enabled}
                onChange={(e) => updateSetting('taxDeduction.enabled', e.target.checked)}
              />
              <span>Enable TDS Deduction</span>
            </label>
          </div>

          {!!settings?.taxDeduction?.enabled && (
            <>
              <div className="affiliate-setting-item">
                <label>TDS Rate (%)</label>
                <input
                  type="number"
                  value={settings.taxDeduction.rate}
                  onChange={(e) => updateSetting('taxDeduction.rate', parseInt(e.target.value || '0', 10))}
                  className="affiliate-payout-input"
                />
              </div>

              <div className="affiliate-setting-item">
                <label>TDS Threshold (₹)</label>
                <input
                  type="number"
                  value={settings.taxDeduction.tdsThreshold}
                  onChange={(e) => updateSetting('taxDeduction.tdsThreshold', parseInt(e.target.value || '0', 10))}
                  className="affiliate-payout-input"
                />
                <small>Apply TDS only above this amount</small>
              </div>
            </>
          )}
        </div>

        <div className="affiliate-settings-section">
          <h2>Payout Schedule</h2>
          <div className="affiliate-setting-item">
            <label>Frequency</label>
            <select
              value={settings?.payoutSchedule?.frequency || 'Weekly'}
              onChange={(e) => updateSetting('payoutSchedule.frequency', e.target.value)}
              className="affiliate-payout-select"
            >
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Bi-weekly">Bi-weekly</option>
              <option value="Monthly">Monthly</option>
            </select>
          </div>

          <div className="affiliate-setting-item">
            <label>Payout Days</label>
            <div className="affiliate-days-selector">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                <label key={day} className="affiliate-checkbox-label">
                  <input
                    type="checkbox"
                    checked={payoutDays.includes(day)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateSetting('payoutSchedule.days', Array.from(new Set([...payoutDays, day])));
                      } else {
                        updateSetting('payoutSchedule.days', payoutDays.filter((d) => d !== day));
                      }
                    }}
                  />
                  <span>{day}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="affiliate-setting-item">
            <label>Cutoff Time</label>
            <input
              type="time"
              value={settings?.payoutSchedule?.cutoffTime || '18:00'}
              onChange={(e) => updateSetting('payoutSchedule.cutoffTime', e.target.value)}
              className="affiliate-payout-input"
            />
          </div>
        </div>

        <div className="affiliate-settings-section">
          <h2>Notification Settings</h2>
          <div className="affiliate-notification-settings">
            <label className="affiliate-checkbox-label">
              <input
                type="checkbox"
                checked={!!settings?.notificationSettings?.emailNotifications}
                onChange={(e) => updateSetting('notificationSettings.emailNotifications', e.target.checked)}
              />
              <span>Email Notifications</span>
            </label>

            <label className="affiliate-checkbox-label">
              <input
                type="checkbox"
                checked={!!settings?.notificationSettings?.smsNotifications}
                onChange={(e) => updateSetting('notificationSettings.smsNotifications', e.target.checked)}
              />
              <span>SMS Notifications</span>
            </label>

            <label className="affiliate-checkbox-label">
              <input
                type="checkbox"
                checked={!!settings?.notificationSettings?.payoutConfirmation}
                onChange={(e) => updateSetting('notificationSettings.payoutConfirmation', e.target.checked)}
              />
              <span>Payout Confirmation</span>
            </label>

            <label className="affiliate-checkbox-label">
              <input
                type="checkbox"
                checked={!!settings?.notificationSettings?.rejectionReasons}
                onChange={(e) => updateSetting('notificationSettings.rejectionReasons', e.target.checked)}
              />
              <span>Rejection Reasons</span>
            </label>
          </div>
        </div>

        <div className="affiliate-settings-actions">
          <button
            className="affiliate-payout-btn-primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <FaCog className="affiliate-spinner" />
                Saving...
              </>
            ) : (
              <>
                <FaSave />
                Save Settings
              </>
            )}
          </button>

          {saved && (
            <div className="affiliate-success-message">
              Settings saved successfully!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}